/**
 * Nice domain rounding and compact number formatting.
 */

/**
 * Round a number to a "nice" value (1, 2, 5 multiples x power of 10).
 * Based on Heckbert's "Nice Numbers for Graph Labels" algorithm.
 */
function niceNum(range, round) {
  if (range === 0) return 1;
  const exponent = Math.floor(Math.log10(Math.abs(range)));
  const fraction = range / Math.pow(10, exponent);
  let nice;
  if (round) {
    if (fraction < 1.5) nice = 1;
    else if (fraction < 3) nice = 2;
    else if (fraction < 7) nice = 5;
    else nice = 10;
  } else {
    if (fraction <= 1) nice = 1;
    else if (fraction <= 2) nice = 2;
    else if (fraction <= 5) nice = 5;
    else nice = 10;
  }
  return nice * Math.pow(10, exponent);
}

/**
 * Compute aesthetically pleasing axis bounds for a set of values.
 */
export function niceDomain(values, {
  padding = 0.05,
  includeZero = false,
  minOverride,
  maxOverride,
  desiredTicks = 6,
} = {}) {
  const hasMinOverride = minOverride != null && String(minOverride).trim() !== '';
  const hasMaxOverride = maxOverride != null && String(maxOverride).trim() !== '';

  const nums = values.filter((v) => Number.isFinite(v));
  if (nums.length === 0) return [hasMinOverride ? parseFloat(minOverride) : 0, hasMaxOverride ? parseFloat(maxOverride) : 1];

  let lo = Math.min(...nums);
  let hi = Math.max(...nums);

  if (lo === hi) {
    const nudge = Math.abs(lo) * 0.1 || 1;
    lo -= nudge;
    hi += nudge;
  }

  const allNonNeg = lo >= 0;
  const allNonPos = hi <= 0;
  const range = hi - lo;

  lo -= range * padding;
  hi += range * padding;

  if (allNonNeg) lo = includeZero ? 0 : Math.max(0, lo);
  if (allNonPos) hi = includeZero ? 0 : Math.min(0, hi);

  if (includeZero) {
    lo = Math.min(0, lo);
    hi = Math.max(0, hi);
  }

  const step = niceNum((hi - lo) / Math.max(1, desiredTicks - 1), true);
  const niceMin = hasMinOverride ? parseFloat(minOverride) : Math.floor(lo / step) * step;
  const niceMax = hasMaxOverride ? parseFloat(maxOverride) : Math.ceil(hi / step) * step;

  return [niceMin, niceMax];
}

/**
 * Format a number using compact notation (K, M, B) when its magnitude is large.
 */
export function formatCompactNumber(value, decimals = 1) {
  if (value == null || isNaN(value)) return '';
  const abs = Math.abs(value);
  let scaled, suffix;
  if (abs >= 1e9)      { scaled = value / 1e9; suffix = 'B'; }
  else if (abs >= 1e6) { scaled = value / 1e6; suffix = 'M'; }
  else if (abs >= 1e4) { scaled = value / 1e3; suffix = 'K'; }
  else return Number.isInteger(value) ? String(value) : value.toFixed(decimals);
  const fixed = scaled.toFixed(decimals);
  const trimmed = fixed.replace(/\.?0+$/, '');
  return trimmed + suffix;
}

/**
 * Return a tick formatter that uses a consistent suffix (K/M/B) for every tick.
 */
export function getCompactTickFormatter(maxValue) {
  const abs = Math.abs(maxValue);
  if (abs >= 1e9)      return (v) => (v === 0 ? '0' : (v / 1e9).toFixed(1).replace(/\.?0+$/, '') + 'B');
  if (abs >= 1e6)      return (v) => (v === 0 ? '0' : (v / 1e6).toFixed(1).replace(/\.?0+$/, '') + 'M');
  if (abs >= 1e4)      return (v) => (v === 0 ? '0' : (v / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K');
  return null;
}
